import type { PlanFeatureFlags } from "@/react/types";

import { BooleanFeature } from "./BooleanFeature";
import { FeatureCell } from "./FeatureCell";
import { MappedFeature } from "./MappedFeature";
import { NumberFeature } from "./NumberFeature";
import { usePlans } from "./PlansContext";
import { StringFeature } from "./StringFeature";
import { TooltipIcon } from "./TooltipIcon";

interface PlanFeatureFlagProps {
  feature: string;
  mapper?: (value: number) => string;
  suffix?: string;
  title: string;
  tooltipKey?: string;
}

export const PlanFeatureFlag = ({
  title,
  feature,
  suffix,
  mapper,
  tooltipKey,
}: PlanFeatureFlagProps) => {
  const { compareTablePlanFeatures } = usePlans();

  if (!compareTablePlanFeatures) return null;

  const tiers = Object.keys(compareTablePlanFeatures);

  const renderInnerContent = (
    isNumeric: boolean,
    featureValue: number | boolean,
  ) => {
    if (mapper && isNumeric) {
      return <MappedFeature value={mapper(featureValue as number)} />;
    }

    if (isNumeric) {
      return <NumberFeature value={featureValue as number} suffix={suffix} />;
    }

    return <BooleanFeature value={!!featureValue} />;
  };

  const renderPlanFeature = (
    planTier: string,
    planFeatureFlag: PlanFeatureFlags,
  ) => {
    const featureValue = planFeatureFlag[feature as keyof PlanFeatureFlags];
    const isNumeric = typeof featureValue === "number";

    return (
      <FeatureCell key={planTier}>
        {renderInnerContent(isNumeric, featureValue)}
      </FeatureCell>
    );
  };

  return (
    <>
      <FeatureCell>
        <StringFeature className="pr-5" value={title} hideCheckIcon />
        {tooltipKey && (
          <TooltipIcon tooltipKey={tooltipKey} tooltipProps={{}} />
        )}
      </FeatureCell>
      {tiers.map(tier =>
        renderPlanFeature(tier, compareTablePlanFeatures[tier]),
      )}
    </>
  );
};
